import React, {
	PropsWithChildren,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react"

import { CoreConfig } from "@cirrus/core/src/shared/config"

import { WithI18n } from "./I18n"
import { Translations, TranslationsService } from "./service"
import { useCustomerInfo } from "../../../CustomerInfo"
import { AvailableLanguages, LanguageCode } from "../../../CustomerInfo/types"

export const WithTranslations = ({ children, service }: PropsWithChildren & {
	service: TranslationsService
}) => {
	const [state, setState] = useState(new TranslationsState())
	const customerInfo = useCustomerInfo()

	const defaultLanguage = useMemo(() => {
		return service.getPersistedLanguage() ?? customerInfo.data?.defaultLanguage
	}, [customerInfo.data?.defaultLanguage])

	const availableLanguages = customerInfo.data?.availableLanguages ?? {}

	const changeLanguageCode = (language: LanguageCode) => {
		service.persistLanguage(language)
		setState((prev) => ({ ...prev, language }))
	}

	const requestTranslationsAndSwitchToLanguage = (language: LanguageCode) => {
		setState((prev) => ({ ...prev, loading: true, error: null }))

		service.getTranslations(language).then(({ data }) => {
			service.persistLanguage(language)

			setState((prev) => ({
				language,
				loading: false,
				error: null,
				translations: {
					...prev.translations,
					[language]: data
				},
			}))
		}).catch((error) => setState((prev) => ({
			...prev, loading: false, error
		})))
	}

	const changeLanguage = useCallback((language: LanguageCode) => {
		state.translations?.[language] ? changeLanguageCode(language) : requestTranslationsAndSwitchToLanguage(language)

		document.documentElement.setAttribute("lang", CoreConfig.params.i18n.locales.byCode[language].shorthand)
	}, [service, state.language])

	useEffect(() => {
		!!defaultLanguage && changeLanguage(defaultLanguage)
	}, [service, defaultLanguage])

	return <TranslationsContext.Provider value={{
		...state,
		changeLanguage,
		availableLanguages
	}}>
		<WithI18n>{children}</WithI18n>
	</TranslationsContext.Provider>
}

class TranslationsState {
	language: LanguageCode = "en-GB"
	translations: Partial<Record<LanguageCode, Translations>> | null = null
	loading: boolean = false
	error: null | unknown = null
}

type Context = TranslationsState & {
	availableLanguages: AvailableLanguages,
	changeLanguage: (language: LanguageCode) => void,
}

const TranslationsContext = React.createContext<Context>({
	...new TranslationsState(),
	availableLanguages: {},
	changeLanguage: (language) => console.debug(language),
})

export const useTranslationsContext = () => React.useContext(TranslationsContext)

// TODO: Refactor. The app uses "loginLang" & "urlLang" but what's the
//  difference? Both got stored from the login/start page. Kept that
//  because of risks to break the old code.
